import { map, switchMap, finalize, retry, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of, throwError, BehaviorSubject, combineLatest } from 'rxjs';
import { AutosaveFormDetailsParams, AutoSaveOwnersDetailsStatus, AutosaveResponseData, SubmissionFormData, SubmissionFormDiffData, AutosaveTriggerStatus, AutosaveValidationInputData, AutosaveValidationData, AutosaveTriggerData } from '../../form-details.model';
import { FormGroup } from '@angular/forms';
import { AutosaveValidationService } from './autosave-validation.service';
import { catchError, tap } from 'rxjs/operators';
import { AutosaveFormStepsService } from './autosave-form-steps.service';
import { FormDetailsActionsService } from '../actions/form-details-actions.service';
import { AutosavePostFunctionsService } from './autosave-post-functions.service';
import { DeleteRequirementDocumentData, SaveRequirementDocumentData } from '../../../../../../domain/models/submissions.model';
import { AutosaveDocumentsStepService } from './autosave-documents-step.service';
import { ApplicationStage, MessageService } from 'common';
import { RequirementData } from '../../../../shared/components/requirement-documents-section/requirement-documents-panel.model';
import { SubmissionsFacade } from '../../../../../../domain/+state/submissions.facade';
import { SubmissionDetailsActionsService } from '../../../../actions/submission-details-actions.service';
import { SubmissionDetailsAppBarStatusType } from '../../../../shared/components/submission-details-app-bar-title/submission-details-app-bar-title.model';
import { ApplicationData } from '../../../../../../domain/models/application.model';

@Injectable({
  providedIn: 'root'
})
export class AutosaveFormDetailsFacade {
  private _hasAnyChangesSaved = false;
  private pendingDiffPayload: SubmissionFormDiffData | null = null;
  private processingDiffPayload: SubmissionFormDiffData | null = null;
  private pendingEffectivePayload: SubmissionFormData | null = null;
  private lastSavedEffectivePayload: SubmissionFormData | null = null;
  private autosaveParams: AutosaveFormDetailsParams = null;
  private readonly isFormAutosaveInProgressSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly isDocumentsAutosaveInProgressSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isAutosaveInProgress$: Observable<boolean> = combineLatest([
    this.isFormAutosaveInProgressSubject.asObservable(),
    this.isDocumentsAutosaveInProgressSubject.asObservable()
  ]).pipe(
    map(([formAutosave, documentsAutosave]) => {
      const isInProgress = formAutosave || documentsAutosave;
      if (!isInProgress && this._hasAnyChangesSaved) {
        this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.Success);
      }
      return isInProgress;
    })
  );

  constructor(
    private readonly validationService: AutosaveValidationService,
    private readonly autosaveFormStepsService: AutosaveFormStepsService,
    private readonly formDetailsActions: FormDetailsActionsService,
    private readonly submissionDetailsActions: SubmissionDetailsActionsService,
    private readonly autosaveDocumentsStepService: AutosaveDocumentsStepService,
    private readonly postFunctionsService: AutosavePostFunctionsService,
    private readonly messageService: MessageService,
    private readonly submissionsFacade: SubmissionsFacade,
  ) { }

  initData(params: AutosaveFormDetailsParams, initialPayload: SubmissionFormData): void {
    this.autosaveParams = { ...params };
    this.lastSavedEffectivePayload = { ...initialPayload };
    this.formDetailsActions.autosaveResponse({ params: this.autosaveParams });
  }

  autosaveFormData(submissionForm: FormGroup): AutosaveTriggerData {
    const validationData = this.validateAutosaveStatus(submissionForm);
    if (!validationData.hasAnySavableData) {
      return this.returnTriggerData(AutosaveTriggerStatus.NoSavableChanges);
    }

    if (validationData.isAutosaveBlocked) {
      this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.MissingCompanyNameError);
      return this.returnTriggerData(AutosaveTriggerStatus.Blocked);
    }

    if (!this.isFormAutosaveInProgressSubject.value) {
      this.lastSavedEffectivePayload = { ...validationData.effectivePayload };
      this.isFormAutosaveInProgressSubject.next(true);
      this.processingDiffPayload = validationData.diffPayload;
      this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.InProgress);
      this.doSaveFormData(validationData.diffPayload);
      return this.returnTriggerData(AutosaveTriggerStatus.Processing);
    }

    this.pendingDiffPayload = validationData.diffPayload;
    this.pendingEffectivePayload = validationData.effectivePayload;
    return this.returnTriggerData(AutosaveTriggerStatus.Queuing);
  }

  saveDocuments(documentsToSave: SaveRequirementDocumentData[], application: ApplicationData): Observable<RequirementData[]> {
    if (!documentsToSave.length) {
      return of(undefined);
    }

    if (!this.autosaveParams?.applicationId) {
      console.error("Cannot save documents for undefined applicationId");
      this.messageService.error("A company name is required in order to upload documents.", "Cannot upload documents");
      return of(undefined);
    }

    this.isDocumentsAutosaveInProgressSubject.next(true);
    this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.InProgress);
    return this.autosaveDocumentsStepService.saveDocuments(this.autosaveParams?.applicationId, documentsToSave, application)
      .pipe(
        tap(() => this._hasAnyChangesSaved = true),
        catchError((error) => this.onAutosaveError(error)),
        finalize(() => this.onSaveDocumentsComplete()));
  }

  deleteDocument(data: DeleteRequirementDocumentData, application: ApplicationData): Observable<RequirementData> {
    this.isDocumentsAutosaveInProgressSubject.next(true);
    this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.InProgress);
    return this.autosaveDocumentsStepService.deleteDocument(this.autosaveParams?.applicationId, data, application)
      .pipe(
        tap(() => this._hasAnyChangesSaved = true),
        catchError((error) => this.onAutosaveError(error)),
        finalize(() => this.onSaveDocumentsComplete()));
  }

  submit(): Observable<void> {
    return this.waitForAutosaveCompletion().pipe(
      switchMap(() => this.submissionsFacade.updateApplicationData(this.autosaveParams.applicationId, { stage: ApplicationStage.ApplicationCreated })
        .pipe(retry(3), map(() => {
          this._hasAnyChangesSaved = false;
          return undefined;
        }))
      ))
  }

  isAutosaveInProgress(): boolean {
    return this.isFormAutosaveInProgressSubject.value || this.isDocumentsAutosaveInProgressSubject.value;
  }

  waitForAutosaveCompletion(): Observable<boolean> {
    if (!this.isAutosaveInProgress()) {
      return of(this._hasAnyChangesSaved);
    }
    return this.isAutosaveInProgress$.pipe(filter((isInProgress) => !isInProgress), map(() => this._hasAnyChangesSaved));
  }

  clearAllData(): void {
    this.clearFormProcessingData();
    this.autosaveParams = null;
    this.lastSavedEffectivePayload = {};
    this._hasAnyChangesSaved = false;
    this.isDocumentsAutosaveInProgressSubject.next(false);
    this.formDetailsActions.autosaveResponse(null);
  }

  private validateAutosaveStatus(submissionForm: FormGroup): AutosaveValidationData {
    const validationInputData: AutosaveValidationInputData = {
      submissionForm,
      lastSavedEffectivePayload: this.lastSavedEffectivePayload,
      pendingDiffPayload: this.pendingDiffPayload,
      processingDiffPayload: this.processingDiffPayload,
      autosaveParams: this.autosaveParams,
    }
    const validationData = this.validationService.validateAutosaveStatus(validationInputData);
    if (validationData.canClearPendingPayload) {
      this.pendingDiffPayload = null;
      this.pendingEffectivePayload = null;
    }

    return validationData;
  }

  private returnTriggerData(status: AutosaveTriggerStatus): AutosaveTriggerData {
    return { status, hasAnyChangesSaved: this._hasAnyChangesSaved };
  }

  private doSaveFormData(diffPayload: SubmissionFormDiffData): void {
    this.autosaveFormStepsService.saveAllSteps(diffPayload, this.autosaveParams)
      .pipe(
        tap((data: AutosaveResponseData) => this.onSaveFormDataSuccess(data)),
        catchError((error) => this.onAutosaveError(error)),
        finalize(() => this.onSaveFormDataComplete())
      ).subscribe();
  }

  private onSaveFormDataSuccess(data: AutosaveResponseData): void {
    this._hasAnyChangesSaved = true;
    this.autosaveParams = { ...data.params };
    this.updateOwnersWithResponse(data?.ownersStatus);
    this.formDetailsActions.autosaveResponse({ params: this.autosaveParams, ownersStatus: data?.ownersStatus });
  }

  private updateOwnersWithResponse(ownersStatus: AutoSaveOwnersDetailsStatus): void {
    if (!this.postFunctionsService.hasAnyOwnersChangesToUpdate(ownersStatus)) {
      return;
    }

    if (this.lastSavedEffectivePayload?.ownersInformation) {
      this.lastSavedEffectivePayload.ownersInformation = this.postFunctionsService.updateOwnersIdsWithResponse(this.lastSavedEffectivePayload.ownersInformation, ownersStatus);
    }

    if (this.pendingEffectivePayload?.ownersInformation) {
      this.pendingEffectivePayload.ownersInformation = this.postFunctionsService.updateOwnersIdsWithResponse(this.pendingEffectivePayload.ownersInformation, ownersStatus);
      this.pendingDiffPayload = this.validationService.getDiffPayload(this.lastSavedEffectivePayload, this.pendingEffectivePayload);
    }
  }

  private onAutosaveError(error: unknown): Observable<never> {
    console.error("Autosave form details error: ", error);
    this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.GeneralError);
    return throwError(error);
  }

  private onSaveFormDataComplete(): void {
    if (this.pendingDiffPayload && this.pendingEffectivePayload) {
      this.lastSavedEffectivePayload = { ...this.pendingEffectivePayload };
      this.processingDiffPayload = this.pendingDiffPayload;
      this.pendingDiffPayload = null;
      this.pendingEffectivePayload = null;
      this.updateAutosaveStatus(SubmissionDetailsAppBarStatusType.InProgress);
      this.doSaveFormData(this.processingDiffPayload);
    } else {
      this.clearFormProcessingData();
    }
  }

  private onSaveDocumentsComplete(): void {
    this.isDocumentsAutosaveInProgressSubject.next(false);
  }

  private updateAutosaveStatus(statusType: SubmissionDetailsAppBarStatusType): void {
    this.submissionDetailsActions.updateSubmissionDetailsAppBarTitle({ statusType });
  }

  private clearFormProcessingData(): void {
    this.pendingDiffPayload = null;
    this.pendingEffectivePayload = null;
    this.processingDiffPayload = null;
    this.isFormAutosaveInProgressSubject.next(false);
  }
}
